import { useEffect, useRef, useState } from 'react'

import { selectAuthModalInPublicLink, selectAuthModalMode, selectAuthModalType, selectIsAuthModalOpen } from "features/common/model/selectors/appSelectors"
import {
  AUTH_MODAL_MODE,
  resetEmailVerification,
  setAuthModalMode,
  setEmailVerificationCanResend,
  setEmailVerificationCode,
  setEmailVerificationEmail,
  setEmailVerificationError,
  setEmailVerificationLoading,
  setEmailVerificationTimeLeft,
  setExpireDate,
  setIsAuthModalOpen,
  setSendToEmail
} from 'features/common/model/slice/appSlice'
import moment from 'moment'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { AppLogo } from 'shared/components/AppLogo'
import { getCurrentAppConfig } from 'shared/config/appConfig'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { Button, Checkbox, Divider, Flex, Form, FormProps, Input, Modal, Typography } from 'shared/ui'
import styled from 'styled-components'

import { INVITE_HASH_PARAM_NAME, removeParamFromURL } from '../../../../shared/utils/urlUtils'
import { NOTIFICATION_TYPES, showNotification } from '../../../common/notifications'
import { AUTH_ERROR_MESSAGES } from '../../model/constants/authErrorMessages'
import { approveSignupThunk, authThunk, createAccountThunk, googleAuthThunk, resendCodeThunk, } from '../../model/slice/userSlice'

import { EmailVerification } from './components/EmailVerification'

type FieldType = {
  email: string
  password: string
  name: string
  remember_me: boolean
}

export const AuthModal = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthModalOpen = useAppSelector(selectIsAuthModalOpen)
  const authModalType = useAppSelector(selectAuthModalType)
  const modalMode = useAppSelector(selectAuthModalMode)
  const authModalInPublicLink = useAppSelector(selectAuthModalInPublicLink)
  const emailVerification = useAppSelector((state: any) => state.app.emailVerification)
  const DEFAULT_REDIRECT_PATH = '/dashboard'
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [emailForVerification, setEmailForVerification] = useState<string>('')
  const errorMessage = errorCode ? AUTH_ERROR_MESSAGES[errorCode] : null
  const { Title } = Typography
  const [form] = Form.useForm()
  const [searchParams] = useSearchParams()
  const queryString = searchParams.toString()
  const fchash = searchParams.get("fchash")
  const [isAuthAfterInvite, setIsAuthAfterInvite] = useState<boolean>(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const config = getCurrentAppConfig()
  const GOOGLE_CLIENT_ID = (config as any).google_client_id
  const googleButtonRef = useRef<HTMLDivElement>(null)
  const [isFormValid, setIsFormValid] = useState(false)

  const values = Form.useWatch([], form)

  const getRedirectUrl = (queryString: string, location: any) => {
    const currentLocationPathName = location.pathname
    const isPublicLinkLocation = currentLocationPathName.startsWith('/links/fs/')
    return !isPublicLinkLocation ? `${DEFAULT_REDIRECT_PATH}?${queryString}` : `${currentLocationPathName}?${queryString}`
  }

  const redirectUrl = getRedirectUrl(queryString, location)

  const isOverlayMode = authModalType === 'overlay'

  useEffect(() => {
    form
      .validateFields()
      .then(() => setIsFormValid(true))
      .catch(() => setIsFormValid(false))
  }, [form, values])

  useEffect(() => {
    if(fchash) {
      setIsAuthAfterInvite(true)
    }
  }, [fchash])

  useEffect(() => {
    if (modalMode === AUTH_MODAL_MODE.SIGNUP && emailForVerification) {
      form.setFieldsValue({ email: emailForVerification })
    }
  }, [modalMode, emailForVerification, form])

  useEffect(() => {
    if (!isModalVisible || !GOOGLE_CLIENT_ID || !googleButtonRef.current) return
    if (modalMode === AUTH_MODAL_MODE.EMAIL_VERIFICATION) return

    const google = (window as any).google
    const container = googleButtonRef.current

    if (!google?.accounts?.id) return

    container.innerHTML = ''

    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
      ux_mode: 'popup',
    })

    google.accounts.id.renderButton(container, {
      theme: 'outline',
      size: 'large',
      width: '100%',
      text: 'continue_with',
    })
  }, [isModalVisible, modalMode])

  const handleGoogleResponse = (response: any) => {
    const jwt_token = response.credential
    const state = JSON.stringify({
      permanent_auth: true,
      device_description: 'web-client',
      need_to_stop_auth: false
    })
    const successCallback = () => {
      dispatch(setIsAuthModalOpen(false))
      navigate(redirectUrl, { replace: true })
    }

    dispatch(googleAuthThunk({ jwt_token, state, successCallback }))
  }

  const handleToggle = () => {
    if(modalMode === AUTH_MODAL_MODE.SIGNUP) {
      dispatch(setAuthModalMode(AUTH_MODAL_MODE.SIGNIN))
    } else {
      dispatch(setAuthModalMode(AUTH_MODAL_MODE.SIGNUP))
    }
    form.resetFields()
    setErrorCode(null)
    setEmailForVerification('')
    dispatch(resetEmailVerification())
  }

  const handleClose = () => {
    dispatch(setIsAuthModalOpen(false))
    dispatch(resetEmailVerification())
    setErrorCode(null)
  }
  const successCallback = () => {
    //commented for testing
    // removeParamFromURL(INVITE_HASH_PARAM_NAME)
    handleClose()
  }

  const errorCallbackSendCode = (error: string) => {
    dispatch(setEmailVerificationError(error))
    dispatch(setEmailVerificationLoading(false))
  }

  const handleFinish: FormProps<FieldType>["onFinish"] = (values) => {


    const errorCallback = (error: string) => {
      setErrorCode(error)
    }

    if (modalMode === AUTH_MODAL_MODE.SIGNUP) {
      dispatch(createAccountThunk({ data: values })).unwrap()
        .then((response) => {
          setEmailForVerification(values.email)
          dispatch(setEmailVerificationEmail(values.email))
          dispatch(setSendToEmail(values.email))

          if(response.expires > 0) {
            dispatch(setExpireDate(moment(response.expires).format('MM.DD.YYYY')))
          }

          dispatch(setAuthModalMode(AUTH_MODAL_MODE.EMAIL_VERIFICATION))
        })
        .catch((error) => {
          if (error.code === 'USER_ALREADY_EXISTS' && values.email === emailForVerification) {
            dispatch(setEmailVerificationEmail(values.email))
            dispatch(setSendToEmail(values.email))
            dispatch(setAuthModalMode(AUTH_MODAL_MODE.EMAIL_VERIFICATION))
            dispatch(resendCodeThunk({ email: values.email })).unwrap().catch((resendError: any) => {
              dispatch(setEmailVerificationError(resendError.status === 403 ? 'Your account is already confirmed. Please try signing in instead.' : 'Something went wrong, please try again later'))
              dispatch(setEmailVerificationLoading(false))
            })
          } else {
            setErrorCode(error.code)
            showNotification({
              type: NOTIFICATION_TYPES.ERROR,
              message: AUTH_ERROR_MESSAGES[error.code] || error.message,
              title: "Error",
            })
          }
        })
    } else {
      dispatch(authThunk({ data: values, successCallback, errorCallback }))
    }
  }

  const handleCodeChange = (code: string) => {
    dispatch(setEmailVerificationCode(code))
    if (emailVerification.error) {
      dispatch(setEmailVerificationError(null))
    }
  }

  const handleCodeSubmit = () => {
    dispatch(setEmailVerificationLoading(true))
    dispatch(approveSignupThunk({ hash: `${emailVerification.email}:${emailVerification.code}`, successCallback, errorCallback: errorCallbackSendCode }))
  }

  const handleResendCode = () => {
    dispatch(setEmailVerificationCanResend(false))
    dispatch(setEmailVerificationTimeLeft(60))
    dispatch(resendCodeThunk({ email: emailVerification.email })).unwrap()
      .catch((error) => {
        dispatch(setEmailVerificationError(error.status === 403 ? 'Your account is already confirmed. Please try signing in instead.' : 'Something went wrong, please try again later'))
        dispatch(setEmailVerificationLoading(false))
      })
  }

  const handleBackToSignup = () => {
    dispatch(setAuthModalMode(AUTH_MODAL_MODE.SIGNUP))
    form.resetFields()
  }

  const handleBackToVerification = () => {
    dispatch(setAuthModalMode(AUTH_MODAL_MODE.EMAIL_VERIFICATION))
  }

  const getTitle = () => {
    if (modalMode === AUTH_MODAL_MODE.EMAIL_VERIFICATION) {
      return ""
    }
    if(isAuthAfterInvite && modalMode === AUTH_MODAL_MODE.SIGNIN) {
      return "Join team"
    }
    if(authModalInPublicLink) {
      return `${modalMode === AUTH_MODAL_MODE.SIGNIN ? "Sign in" : "Sign up"} to continue`
    }
    return modalMode === AUTH_MODAL_MODE.SIGNUP ? "Sign Up" : "Sign In"
  }

  useEffect(() => {
    let timer: NodeJS.Timeout

    if (modalMode === AUTH_MODAL_MODE.EMAIL_VERIFICATION && !emailVerification.canResend && emailVerification.timeLeft > 0) {
      timer = setInterval(() => {
        dispatch(setEmailVerificationTimeLeft(emailVerification.timeLeft - 1))

        if (emailVerification.timeLeft <= 1) {
          dispatch(setEmailVerificationCanResend(true))
          dispatch(setEmailVerificationTimeLeft(60))
        }
      }, 1000)
    }

    return () => {
      if (timer) clearInterval(timer)
    }
  }, [modalMode, emailVerification.canResend, emailVerification.timeLeft, dispatch])



  const renderModalContent = () => {
    if (modalMode === AUTH_MODAL_MODE.EMAIL_VERIFICATION) {
      return (
        <EmailVerification
          email={emailVerification.email}
          code={emailVerification.code}
          onCodeChange={handleCodeChange}
          onCodeSubmit={handleCodeSubmit}
          onResendCode={handleResendCode}
          onBackToSignup={handleBackToSignup}
          timeLeft={emailVerification.timeLeft}
          canResend={emailVerification.canResend}
          error={emailVerification.error}
          loading={emailVerification.isLoading}
        />
      )
    }

    return (
      <>
        <SFlex align="center">
          <AppLogo
            alt="Company Logo"
          />
        </SFlex>

        <SModalTitle>
          <Title>
            {getTitle()}
          </Title>
        </SModalTitle>

        {authModalInPublicLink && (
          <SModalDescription>
            {`Please ${modalMode === AUTH_MODAL_MODE.SIGNIN ? "sign in" : "sign up"} to view shared content in Aivard, a platform for managing digital assets.`}
          </SModalDescription>
        )}

        {isAuthAfterInvite && modalMode === AUTH_MODAL_MODE.SIGNIN && (
          <SModalDescription>
            { "You have been invited to join the team on Aivard, a platform for managing digital content." }
          </SModalDescription>
        )}

        {emailForVerification && modalMode === AUTH_MODAL_MODE.SIGNUP && (
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <SModalButtomText>
              <span>
Back to verification by email:
                {' '}
              </span>

              <Button
                type="link"
                style={{ paddingInline: 4, color: 'var(--color-primary)' }}
                onClick={handleBackToVerification}
              >
                {emailForVerification}
              </Button>
            </SModalButtomText>

            <Divider>
or
            </Divider>
          </div>
        )}

        <SGoogleBtnWrapper ref={googleButtonRef}
          style={{ marginBottom: 16 }}
        />

        {!emailForVerification  && <Divider />}

        <SContentContainer>
          <Form
            layout="vertical"
            onFinish={handleFinish}
            preserve={false}
            form={form}
            requiredMark="optional"
            onValuesChange={() => {
              if (errorCode) setErrorCode(null)
            }}
          >
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: "Enter email" },
                { type: "email", message: "Enter a valid email" },
              ]}
            >
              <Input size="large"
                placeholder="Valid email address"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: "Enter password" },
                { whitespace: true, message: "Password cannot consist of only spaces" },
              ]}
            >
              <Input.Password size="large"
                placeholder="Your password (min 8 chars)"
              />
            </Form.Item>

            {modalMode === AUTH_MODAL_MODE.SIGNUP && (
              <Form.Item
                name="name"
                label="Display name"
                rules={[
                  { required: true, message: "Enter display name" },
                  { min: 4, message: "Display name must be at least 4 characters long" },
                  { whitespace: true, message: "Name cannot consist of only spaces" }]}
              >
                <Input size="large"
                  placeholder="Min 4 chars"
                />
              </Form.Item>
            )}

            <Form.Item>
              <Flex justify="space-between"
                align="center"
              >
                <Form.Item name="remember_me"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>
  Remember me
                  </Checkbox>
                </Form.Item>
              </Flex>
            </Form.Item>

            {errorMessage && (
              <Typography.Text
                type="danger"
                style={{ display: "block", marginBottom: 16 }}
              >
                {errorMessage}
              </Typography.Text>
            )}

            <Form.Item shouldUpdate>
              {() => (
                <Button
                  block
                  type="primary"
                  htmlType="submit"
                  disabled={!isFormValid}
                >
                  {modalMode === AUTH_MODAL_MODE.SIGNIN
                    ? (isAuthAfterInvite ? "Join team" : "Sign in")
                    : "Sign up"
                  }
                </Button>
              )}
            </Form.Item>
          </Form>

          <SModalButtomText>
            <span>
              {modalMode === AUTH_MODAL_MODE.SIGNUP
                ? "Already have an account?"
                : "Don't have an account?"}
            </span>

            <Button
              type="link"
              style={{ paddingInline: 1, color: "var(--color-primary)" }}
              onClick={handleToggle}
            >
              {modalMode === AUTH_MODAL_MODE.SIGNUP ? "Sign In" : "Sign Up"}
            </Button>
          </SModalButtomText>
        </SContentContainer>
      </>
    )
  }

  return (
    <Modal
      open={isAuthModalOpen}
      afterOpenChange={(open) => {
        setIsModalVisible(open)
      }}
      footer={null}
      width={480}
      closable={isOverlayMode && !authModalInPublicLink}
      onCancel={isOverlayMode && !authModalInPublicLink ? handleClose : undefined}
      centered
      destroyOnClose={true}
      styles={{ mask: { backgroundColor: isOverlayMode ? "rgba(0, 0, 0, 0.45)" : "transparent"  } }}
      wrapClassName={`modal-wrapper ${authModalType === 'default' ? modalMode === AUTH_MODAL_MODE.SIGNUP ? "sign-up-modal-wrapper" : "sign-in-modal-wrapper" : ''}`}
    >
      {renderModalContent()}
    </Modal>
  )
}

const SFlex = styled(Flex)`
  position: absolute;
  width: 102px;
  top: -100px;
  left: 50%;
  transform: translateX(-50%);
`

const SContentContainer = styled.div``

const SModalTitle = styled.div`
  text-align: center;
`

const SModalButtomText = styled.div`
  text-align: center;
`

const SModalDescription = styled.p`
  color: rgba(0, 0, 0, 0.88);
  text-align: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  max-width: 360px;
  margin: 0 auto 18px;
`

const SGoogleBtnWrapper = styled.div`
  margin-bottom: 16px;
  & * {
    font-size: 16px !important;
  }
`
